import {Injectable} from "@angular/core";
import {NbToastrService} from "@nebular/theme";
import {TranslateService} from "@ngx-translate/core";
import {Observable, of} from "rxjs";
import {map} from "rxjs/operators";
import {NbToastrConfig} from "@nebular/theme";

@Injectable()
export class NotificationService {

	private readonly DEFAULT_TITLE_KEYS: {[type: string]: string} = {
		error: 'error-messages.title',
		danger: 'error-messages.title',
		warning: 'error-messages.warning',
		success: 'common.success'
	};

	private readonly DEFAULT_ERROR_TEXT_KEY: string = 'error-messages.generic-message';
	private readonly EMPTY_TEXT_KEY: string = 'common.empty-text';

	constructor(private toastrService: NbToastrService, private translator: TranslateService) {
	}

	error(notification: Notification = DefaultErrorNotification, config: Partial<NbToastrConfig> = {preventDuplicates: true, duration: 2000, toastClass:'toast-full-width'}): void {
		this.displayNotification(notification, config, 'error');
	}

	success(notification: Notification = DefaultSuccessNotification, config: Partial<NbToastrConfig> = {preventDuplicates: true, duration: 2000}) {
		this.displayNotification(notification, config, 'success');
	}

	warning(notification: Notification = DefaultWarningNotification, config: Partial<NbToastrConfig> = {preventDuplicates: true, duration: 2000}) {
		this.displayNotification(notification, config, 'warning');
	}

	private displayNotification(notification: Notification, config: Partial<NbToastrConfig>, type: NotificationType) {
		type = type === "error" ? "danger" : type;
		this.resolveNotification(notification, type).subscribe(not => this.toastrService[type](not.text, not.title, config));
		this.showInConsoleIfNeeded(notification);
	}

	private showInConsoleIfNeeded(notification: Notification): void {
		if (shouldLogToConsole(notification)) {
			let level: string = notification.level || 'info'
			console[level].call(notification.consoleMsg)
		}
	}

	private resolveNotification(notification: Notification, type: NotificationType): Observable<StringText & StringTitle> {
		return of({
			title: hasTitle(notification) ? notification.title : this.getTitle(notification, type),
			text: hasText(notification) ? notification.text : this.getText(notification, type),
		}).pipe(
			map(notif => ({
				text: notif.text,
				title: notif.title,
			}))
		);
	}

	private getTitle(notification: Notification, type: NotificationType): string {
		return this.translator.instant((notification as TranslatedTitle).titleKey || this.DEFAULT_TITLE_KEYS[type]);
	}

	private getText(notification: Notification, type: NotificationType): string {
		let key = hasTextKey(notification) ? notification.textKey : (type === 'danger' ? this.DEFAULT_ERROR_TEXT_KEY : this.EMPTY_TEXT_KEY);
		return this.translator.instant(key);
	}
}

function shouldLogToConsole(notification: Notification): notification is ConsoleLog {
	return (notification as ConsoleLog).consoleMsg != null;
}

function hasText(notification: Notification): notification is StringText {
	return (notification as StringText).text != null;
}

function hasTextKey(notification: Notification): notification is TranslatedText {
	return (notification as TranslatedText).textKey != null;
}

function hasTitle(notification: Notification): notification is StringTitle {
	return (notification as StringTitle).title != null;
}

const DefaultErrorNotification: TranslatedText & TranslatedTitle = {
	textKey: 'error-messages.generic-message',
	titleKey: 'error-messages.title'
}

const DefaultWarningNotification: TranslatedTitle = {
	titleKey: 'error-messages.warning'
}

const DefaultSuccessNotification: TranslatedTitle = {
	titleKey: 'common.success',
}

export type Notification = ConsoleLog | StringText | StringTitle | TranslatedText | TranslatedTitle |
	((StringText & StringTitle) | (TranslatedText & TranslatedTitle)) |
	(StringText & TranslatedTitle) | (TranslatedText & StringTitle) |
	(ConsoleLog & ((StringText & StringTitle) | (TranslatedText & TranslatedTitle))) |
	(ConsoleLog & ((StringText & TranslatedTitle) | (TranslatedText & StringTitle)))

export type TranslatedText = {
	textKey: string;
}

export type TranslatedTitle = {
	titleKey: string;
}

export type StringText = {
	text: string;
}

export type StringTitle = {
	title: string;
}

export type ConsoleLog = {
	consoleMsg: any;
	level?: 'log' | 'info' | 'warn' | 'error'
}

type NotificationType = 'warning' | 'success' | 'error' | 'danger';
